/**
 * Loads a route chunk, retries once, then reloads a single time.
 * A session flag stops a second automatic reload when the new document is still stale.
 *
 * @format
 */

import { lazy } from 'react';

const RELOAD_KEY = 'mw:chunk-reload';

export const isChunkLoadError = (error) => {
  const message = error?.message || '';
  return (
    error?.name === 'ChunkLoadError' ||
    message.includes('Loading chunk') ||
    message.includes('Failed to fetch dynamically imported module') ||
    message.includes('error loading dynamically imported module') ||
    message.includes('Importing a module script failed')
  );
};

const readReloadFlag = () => {
  try {
    return sessionStorage.getItem(RELOAD_KEY) === '1';
  } catch {
    return null;
  }
};

const writeReloadFlag = () => {
  try {
    sessionStorage.setItem(RELOAD_KEY, '1');
    return true;
  } catch {
    return false;
  }
};

export const clearChunkReloadFlag = () => {
  try {
    sessionStorage.removeItem(RELOAD_KEY);
  } catch {
    // A blocked storage API must not fail a chunk that already loaded.
  }
};

export const loadWithRetry = async (importer) => {
  try {
    const module = await importer();
    clearChunkReloadFlag();
    return module;
  } catch (error) {
    if (!isChunkLoadError(error)) {
      throw error;
    }
  }

  try {
    const module = await importer();
    clearChunkReloadFlag();
    return module;
  } catch (error) {
    if (!isChunkLoadError(error)) {
      throw error;
    }

    // null means storage is blocked. Reloading then could not record the guard.
    if (readReloadFlag() !== false || !writeReloadFlag()) {
      throw error;
    }

    window.location.reload();
    return new Promise(() => {});
  }
};

export const lazyWithRetry = (importer) => lazy(() => loadWithRetry(importer));
